/**
 * Histopathology Home – New Bill.
 * Migrated from admin4.0 HistoNewBill.js; design, layout and flow preserved.
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DateTime } from "luxon";
import { AllCountries, getStatesOfCountry, getAllStates, getCitiesOfState } from "../../Utility/CountryStateCity";
import { getCachevalue, clearCachevalue } from "../../Utility/Services/CacheProviderService";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import { Demo_Layout_Default_JSON } from "../../Utility/Constants";
import CommonValidation from "../../Utility/CommonValidation";
import { ImagePaths } from "../../Utility/ImagePaths";
import HistoBillingTbl from "./HistoBillingTbl";
import "../../Styles/Configuration.css";

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
const TITLES = ["", "Dr", "Mr", "Ms", "Mrs", "Master", "Baby", "Baby of", "Child"];

/** field_name → label for API field_order mapping (same as DemographicsLayout.js) */
const FIELD_LABEL_MAP = Object.fromEntries(
  Demo_Layout_Default_JSON.map((f) => [f.field_name, f.label])
);
const DEMO_FIELD_ORDER_URL = Serviceurls.HISTOPATHOLOGY_DEMOGRAPHIC_FIELD_ORDER;

const getInitialFormState = (getItem) => {
  const get = getItem || (() => null);
  const stateName = get("state_name") || "";
  const stateList = getStatesOfCountry("IN");
  const stateMatch = stateList?.find((item) => item?.name?.toLowerCase() === stateName.toLowerCase());
  const stateCode = stateMatch?.isoCode || "TN";
  const cityList = getCitiesOfState("IN", stateCode) || [];
  return {
    mobile_no: "",
    first_name: "",
    dob: null,
    gender: "",
    age: "",
    title: "",
    middle_name: "",
    surname: "",
    address_1: "",
    address_2: "",
    city: get("city_name") || "",
    cityName: cityList,
    state: "",
    district: "",
    country: "",
    pin_code: "",
    employer_id: "",
    employee_id: "",
    email_id: "",
    marital_status: "",
    father_husband_name: "",
    father_husband_age: "",
    mother_name: "",
    mother_age: "",
    occupation: "",
    emergency_contact_person: "",
    emergency_contact_mobile: "",
    relationship: "",
    insurance_company_name: "",
    policy_number: "",
    guardian: "",
    old_referral_no: "",
    blood_group: "",
    caretaker_id: "",
    CorporateList: [],
    InsuranceList: [],
    state_code: stateMatch?.isoCode || "TN",
    country_code: "IN",
    AllCountry: AllCountries(),
    AllStates: stateList,
    refferalDesc: "",
    rchid: "",
    rchError: false,
    selectedFatHusName: "",
    patient_id: "",
    nxt_id: "",
    sample_id: "",
  };
};

export default function NewBill({ isExistBilling = false }) {
  const [openAccordion, setOpenAccordion] = useState(false);
  const [layoutFeilds, setLayoutFeilds] = useState(Demo_Layout_Default_JSON);
  const [mandotoryCount, setMandotoryCount] = useState(5);
  const [formState, setFormState] = useState(() => getInitialFormState(getCachevalue));
  const [commonPatientData, setCommonPatientData] = useState({});
  const [searchResultSpecimen, setSearchResultSpecimen] = useState(null);
  const [quickBillClear, setQuickBillClear] = useState(false);
  const [message, setMessage] = useState({
    isErrorMsgText: "",
    successMsgText: "",
    isErrorMsg: false,
    successMsg: false,
  });

  const errorMessage = useCallback((msg) => {
    setMessage((prev) => ({
      ...prev,
      isErrorMsg: true,
      isErrorMsgText: msg,
    }));
  }, []);

  const successMessage = useCallback((msg) => {
    setMessage((prev) => ({
      ...prev,
      successMsg: true,
      successMsgText: msg,
    }));
  }, []);

  const msgClose = useCallback(() => {
    setMessage((prev) => ({
      ...prev,
      isErrorMsg: false,
      isErrorMsgText: "",
      successMsg: false,
      successMsgText: "",
    }));
  }, []);

  const getDatas = useCallback(() => {
    RestAPIService.getAll(DEMO_FIELD_ORDER_URL)
      .then((res) => {
        if (res?.data?.status === "success") {
          const data = res.data.data || [];
          const len = data.length;
          const last = data[len - 1];
          if (last?.field_order?.length) {
            const uiFields = last.field_order
              .slice()
              .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
              .map((f) => ({
                id: f.index,
                field_name: f.field_name,
                label: FIELD_LABEL_MAP[f.field_name] ?? f.field_name,
                is_active: true,
                is_default: !!f.is_mandatory,
              }));
            setLayoutFeilds(uiFields);
          } else {
            setLayoutFeilds(Demo_Layout_Default_JSON);
          }
          const count = last?.mandatory_field_count;
          setMandotoryCount(count != null ? count : 5);
        } else {
          errorMessage(res?.data?.message || "Failed to load layout");
        }
      })
      .catch((err) => errorMessage(err?.response?.data?.message || err?.message));

    RestAPIService.getAll(Serviceurls.HISTOPATHOLOGY_CORPORATE_CONFIG)
      .then((res) => {
        if (res?.data?.status === "success") {
          setFormState((prev) => ({ ...prev, CorporateList: res.data.data || [] }));
        }
      })
      .catch(() => {});

  }, [errorMessage]);

  useEffect(() => {
    const stateName = getCachevalue("state_name");
    const cityName = getCachevalue("city_name");
    const list = getStatesOfCountry("IN");
    const match = list?.find((item) => item?.name?.toLowerCase() === (stateName || "").toLowerCase());
    const stateList = getStatesOfCountry("IN");
    const stateCode = match?.isoCode || "TN";
    const cityList = getCitiesOfState("IN", stateCode) || [];
    setFormState((prev) => ({
      ...prev,
      city: cityName || prev.city,
      cityName: cityList,
      AllStates: stateList,
      state_code: stateCode,
    }));
  }, []);

  useEffect(() => {
    getDatas();
  }, [getDatas]);

  useEffect(() => {
    setFormState((prev) => ({
      ...prev,
      state: prev.AllStates?.find((s) => s.isoCode === prev.state_code)?.name ?? prev.state,
      country: prev.AllCountry?.find((c) => c.isoCode === prev.country_code)?.name ?? prev.country,
    }));
  }, [formState.pin_code, formState.city, formState.state_code, formState.country_code]);

  const handleAgeCalculation = (date) => {
    if (!date) return "";
    const d = date instanceof Date ? date : new Date(date);
    return new Date().getFullYear() - d.getFullYear();
  };

  const handleFindApproxDob = (ageEntered) => {
    const year = +ageEntered;
    if (!year) return null;
    return DateTime.now().minus({ years: year }).toJSDate();
  };

  const handleChange = useCallback((field, value) => {
    setFormState((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "title") {
        if (value === "Mr") next.gender = "m";
        else if (value === "Ms" || value === "Mrs") next.gender = "f";
        if (value === "Mrs") next.marital_status = "Married";
      }
      return next;
    });
  }, []);

  const getPincodeToCity = useCallback(
    (pincode) => {
      fetch(`https://api.postalpincode.in/pincode/${pincode}`)
        .then((r) => r.json())
        .then((response) => {
          if (response?.[0]?.Status === "Success") {
            const data = response[0].PostOffice;
            if (data?.length) {
              const po = data[0];
              const apiState = po?.State === "Pondicherry" ? "Puducherry" : po?.State;
              const states = getStatesOfCountry("IN");
              const stateCode = states.find((s) => s.name === apiState)?.isoCode;
              setFormState((prev) => ({
                ...prev,
                city: prev.city || po.District,
                country_code: prev.country_code || "IN",
                AllStates: states,
                state_code: prev.state_code || stateCode || prev.state_code,
                cityName: [po.District],
              }));
            }
          }
        })
        .catch((e) => errorMessage(e?.message));
    },
    [errorMessage]
  );

  const activeArray = layoutFeilds?.filter((item) => item.is_active === true) || [];
  const splitCount = Math.min(Math.max(0, +mandotoryCount), activeArray.length);
  const mandatoryArray = activeArray.slice(0, splitCount);
  const nonMandatoryArray = activeArray.slice(splitCount, activeArray.length);
  const listMandatory = splitCount > 6 ? activeArray.slice(0, 6) : mandatoryArray;
  const remainMandatoryList = activeArray.slice(6, splitCount);

  const renderTextBox = useCallback(
    (data, type) => {
      const isMandatory = type === "mandatory" || data.is_default;
      const labelSuffix = isMandatory ? "*" : "";
      const disabled = false;

      const boxSx = {
        marginTop: "2.5vh",
        marginLeft: "1vw",
        width:
          data.label === "Age" || data.label === "Title"
            ? "7vw"
            : data.label === "Gender"
            ? "10vw"
            : "12vw",
      };

      if (data.label === "Date of Birth") {
        const dobVal = formState.dob;
        const dateStr =
          dobVal instanceof Date
            ? DateTime.fromJSDate(dobVal).toFormat("yyyy-MM-dd")
            : dobVal && typeof dobVal === "string"
            ? dobVal.slice(0, 10)
            : "";
        return (
          <Box key={data.field_name} sx={boxSx}>
            <TextField
              disabled={disabled}
              label={`${data.label}${labelSuffix}`}
              type="date"
              size="small"
              fullWidth
              value={dateStr}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: DateTime.now().toFormat("yyyy-MM-dd"), min: "1872-01-01" }}
              onChange={(e) => {
                const v = e.target.value;
                if (v) {
                  const jsDate = new Date(v);
                  handleChange("dob", jsDate);
                  handleChange("age", handleAgeCalculation(jsDate));
                } else {
                  handleChange("dob", null);
                  handleChange("age", "");
                }
              }}
            />
          </Box>
        );
      }

      if (data.label === "Gender") {
        return (
          <Box key={data.field_name} sx={boxSx}>
            <FormControl size="small" sx={{ width: "10vw" }}>
              <InputLabel>Gender *</InputLabel>
              <Select
                disabled={disabled}
                label="Gender *"
                value={formState[data.field_name] || ""}
                onChange={(e) => handleChange(data.field_name, e.target.value)}
              >
                <MenuItem value="m">Male</MenuItem>
                <MenuItem value="f">Female</MenuItem>
                <MenuItem value="t">TransGender</MenuItem>
              </Select>
            </FormControl>
          </Box>
        );
      }

      if (data.label === "Marital Status") {
        return (
          <Box key={data.field_name} sx={boxSx}>
            <FormControl size="small" sx={{ width: "12vw" }}>
              <InputLabel>{data.label}{labelSuffix}</InputLabel>
              <Select
                disabled={disabled}
                label={`${data.label}${labelSuffix}`}
                value={formState[data.field_name] || ""}
                onChange={(e) => handleChange(data.field_name, e.target.value)}
              >
                <MenuItem value="Single">Single</MenuItem>
                <MenuItem value="Married">Married</MenuItem>
                <MenuItem value="Divorced">Divorced</MenuItem>
              </Select>
            </FormControl>
          </Box>
        );
      }

      if (data.label === "Employee ID No") {
        return (
          <Box key={data.field_name} sx={boxSx}>
            <TextField
              size="small"
              label={`${data.label}${formState.employer_id ? "*" : ""}`}
              value={formState[data.field_name] || ""}
              onChange={(e) => handleChange(data.field_name, e.target.value.toUpperCase())}
              disabled={disabled || formState.employer_id === "" || formState.employer_id == null}
              inputProps={{ maxLength: 20 }}
            />
          </Box>
        );
      }

      if (data.label === "Title") {
        return (
          <Box key={data.field_name} sx={boxSx}>
            <FormControl size="small" fullWidth>
              <InputLabel>{data.label}{labelSuffix}</InputLabel>
              <Select
                disabled={disabled}
                label={`${data.label}${labelSuffix}`}
                value={formState[data.field_name] || ""}
                onChange={(e) => handleChange(data.field_name, e.target.value)}
                MenuProps={{ style: { maxHeight: 250 } }}
              >
                {TITLES.map((v) => (
                  <MenuItem key={v || "empty"} value={v}>
                    {v || "Title"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );
      }

      if (data.label === "Corporate Name") {
        return (
          <Box key={data.field_name} sx={boxSx}>
            <FormControl size="small" sx={{ width: "12vw" }}>
              <InputLabel>{data.label}{labelSuffix}</InputLabel>
              <Select
                disabled={disabled}
                label={`${data.label}${labelSuffix}`}
                value={formState.employer_id ?? ""}
                onChange={(e) => handleChange("employer_id", e.target.value)}
                MenuProps={{ style: { maxHeight: 250 } }}
              >
                <MenuItem value="">None</MenuItem>
                {formState.CorporateList?.map((item, idx) => (
                  <MenuItem key={idx} value={item.id}>
                    {item.company_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );
      }

      if (data.label === "City" || data.field_name === "city") {
        return (
          <Box key={data.field_name} sx={boxSx}>
            <Autocomplete
              disabled={disabled}
              sx={{ width: "12vw" }}
              size="small"
              options={formState.cityName || []}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option?.name ?? ""
              }
              isOptionEqualToValue={(option, value) =>
                (typeof value === "string" ? value : value?.name) === (option?.name ?? option)
              }
              value={formState.city ?? ""}
              onChange={(e, value) => {
                handleChange("city", value?.name ? value.name : "");
              }}
              clearIcon={false}
              ListboxProps={{ style: { maxHeight: 280 } }}
              onKeyDown={(e) => (e.key === "Enter" ? e.preventDefault() : null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoComplete="new-password"
                  label={`${data.label}${labelSuffix}`}
                  InputProps={{ ...params.InputProps }}
                  onChange={(e) => handleChange("city", e.target.value)}
                />
              )}
            />
          </Box>
        );
      }

      if (data.label === "State" || data.field_name === "state") {
        const states = getStatesOfCountry(formState.country_code) || [];
        return (
          <Box key={data.field_name} sx={boxSx}>
            <FormControl size="small" sx={{ width: "12vw" }}>
              <InputLabel>{data.label}{labelSuffix}</InputLabel>
              <Select
                disabled={disabled}
                value={formState.state_code || ""}
                onChange={(e) => {
                  const stateCode = e.target.value;
                  const countryCode = formState.country_code || "IN";
                  const cities = getCitiesOfState(countryCode, stateCode) || [];
                  handleChange("state_code", stateCode);
                  handleChange("cityName", cities);
                  handleChange("city", "");
                }}
                label={`${data.label}${labelSuffix}`}
              >
                {states?.map((s) => (
                  <MenuItem key={s.isoCode} value={s.isoCode}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );
      }

      if (data.label === "Country" || data.field_name === "country") {
        const countries = formState.AllCountry?.length ? formState.AllCountry : AllCountries();
        return (
          <Box key={data.field_name} sx={boxSx}>
            <FormControl size="small" sx={{ width: "12vw" }}>
              <InputLabel>{data.label}{labelSuffix}</InputLabel>
              <Select
                disabled={disabled}
                value={formState.country_code || ""}
                onChange={(e) => {
                  const countryCode = e.target.value;
                  handleChange("country_code", countryCode);
                  handleChange("state_code", "");
                  handleChange("city", "");
                  handleChange("AllStates", getStatesOfCountry(countryCode));
                  handleChange("cityName", []);
                }}
                label={`${data.label}${labelSuffix}`}
              >
                {countries?.map((c) => (
                  <MenuItem key={c.isoCode} value={c.isoCode}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );
      }

      if (data.label === "Father/Spouse Name") {
        return (
          <Box key={data.field_name} sx={{ ...boxSx, display: "flex" }}>
            <Select
              disabled={disabled}
              size="small"
              value={formState.selectedFatHusName || ""}
              onChange={(e) => handleChange("selectedFatHusName", e.target.value)}
              sx={{ width: "3.5vw", marginRight: 0.5 }}
            >
              <MenuItem value={1}>Father</MenuItem>
              <MenuItem value={2}>Spouse</MenuItem>
            </Select>
            <TextField
              disabled={disabled}
              label={`${data.label}${labelSuffix}`}
              value={formState[data.field_name] || ""}
              onChange={(e) => {
                const v = e.target.value;
                if (/^[ A-Za-z.,/]*$/.test(v) || v === "") handleChange(data.field_name, v);
              }}
              size="small"
              sx={{ width: "10vw" }}
            />
          </Box>
        );
      }

      if (data.label === "RCH ID") {
        return (
          <Box key={data.field_name} sx={boxSx}>
            <TextField
              size="small"
              label={`${data.label}${labelSuffix}`}
              value={formState.rchid || ""}
              error={formState.rchError}
              inputProps={{ maxLength: 13 }}
              onChange={(e) => {
                const v = e.target.value;
                if (!/^[0-9-]*$/.test(v)) return;
                const digits = v.replace(/-/g, "");
                if ((v.match(/-/g) || []).length > 1 || digits.length > 12) return;
                setFormState((prev) => ({
                  ...prev,
                  rchid: v,
                  rchError: v !== "" && digits.length !== 12,
                }));
              }}
            />
          </Box>
        );
      }

      if (data.label === "Blood Group") {
        return (
          <Box key={data.field_name} sx={boxSx}>
            <Autocomplete
              disabled={disabled}
              options={BLOOD_GROUPS}
              value={formState[data.field_name] || ""}
              onChange={(e, val) => handleChange(data.field_name, val)}
              renderInput={(params) => (
                <TextField {...params} label={`${data.label}${labelSuffix}`} size="small" />
              )}
              sx={{ width: "12vw" }}
            />
          </Box>
        );
      }

      if (data.label === "Insurance Company Name") {
        return (
          <Box key={data.field_name} sx={boxSx}>
            <Autocomplete
              disabled={disabled}
              sx={{ width: "12vw" }}
              size="small"
              options={formState.InsuranceList || []}
              getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt?.insurance_name) ?? ""}
              value={formState.insurance_company_name ?? null}
              onChange={(e, val) => handleChange("insurance_company_name", val?.insurance_name ?? "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Insurance Company Name"
                  onChange={(e) => handleChange("insurance_company_name", e.target.value)}
                />
              )}
            />
          </Box>
        );
      }

      if (data.label === "Mobile no") {
        return (
          <Box key={data.field_name} sx={boxSx}>
            <TextField
              disabled={disabled}
              label={`${data.label}${labelSuffix}`}
              size="small"
              value={formState[data.field_name] || ""}
              onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">+91</InputAdornment>
                ),
              }}
              onChange={(e) => {
                const v = e.target.value;
                if ((/^[0-9\b]+$/.test(v) && v.length <= 10) || v === "")
                  handleChange(data.field_name, v);
              }}
            />
          </Box>
        );
      }

      const maxLen =
        ["First Name", "Middle Name", "Surname", "Mother's Name", "Father/Spouse Name", "Emergency Contact Person", "Relationship", "District"].includes(data.label)
          ? 32
          : ["Occupation", "Insurance No"].includes(data.label)
          ? 45
          : data.label === "Email ID"
          ? 50
          : 100;

      return (
        <Box key={data.field_name} sx={boxSx}>
          <TextField
            disabled={disabled}
            autoComplete="off"
            label={`${data.label}${labelSuffix}`}
            variant="outlined"
            size="small"
            value={formState[data.field_name] ?? ""}
            onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
            inputProps={{ maxLength: maxLen }}
            onChange={(e) => {
              const v = e.target.value;
              const isNum = /^[0-9]*$/.test(v);
              const isSpecial = /^[ A-Za-z.,/]*$/.test(v);

              if (["Age", "Father/Spouse Age", "Mother's Age"].includes(data.label)) {
                if ((isNum && +v <= 150) || v === "") {
                  handleChange(data.field_name, v);
                  if (data.label === "Age") {
                    handleChange("dob", handleFindApproxDob(v) || null);
                  }
                } else if (v.includes("/")) {
                  handleChange("age", "");
                  handleChange("dob", null);
                }
              } else if (
                ["First Name", "Middle Name", "Surname", "Emergency Contact Person", "Occupation", "Relationship", "Guardian", "District"].includes(data.label)
              ) {
                if (CommonValidation.alphabetOnly(v) || v === "") handleChange(data.field_name, v);
              } else if (["Father/Spouse Name", "Mother's Name"].includes(data.label)) {
                if (isSpecial || v === "") handleChange(data.field_name, v);
              } else if (data.label === "Pin Code") {
                if ((isNum || v === "") && v.length <= 6) {
                  handleChange(data.field_name, v);
                  if (v.length === 6) getPincodeToCity(v);
                }
              } else if (data.label === "Emergency Mobile No") {
                if ((isNum || v === "") && v.length <= 10) handleChange(data.field_name, v);
              } else if (data.label === "Insurance No") {
                if (/^[A-Za-z0-9]*$/.test(v) || v === "") handleChange(data.field_name, v);
              } else {
                handleChange(data.field_name, v);
              }
            }}
          />
        </Box>
      );
    },
    [
      formState,
      handleChange,
      getPincodeToCity,
    ]
  );

  const assignDemographicsData = useCallback(
    (data) => {
      const countryCode = AllCountries().find((c) => c.name === data.country)?.isoCode || "IN";
      const allStates = getStatesOfCountry(countryCode);
      const stateCode = allStates?.find((s) => s.name === data.state_name)?.isoCode;
      const dob = data.dob != null ? new Date(data.dob) : new Date(data.approx_dob);
      setFormState((prev) => ({
        ...prev,
        mobile_no: data.mobile_number || "",
        first_name: data.first_name || "",
        age: handleAgeCalculation(dob),
        dob,
        gender: data.gender || "",
        title: data.title || "",
        middle_name: data.middle_name || "",
        surname: data.last_name || "",
        address_1: data.address_line_1 || "",
        address_2: data.address_line_2 || "",
        country_code: countryCode || prev.country_code,
        AllStates: allStates || prev.AllStates,
        state_code: stateCode || prev.state_code,
        city: data.city_name || "",
        pin_code: data.pincode || "",
        employer_id: data.employer_id ?? "",
        employee_id: data.id_no || "",
        email_id: data.email || "",
        marital_status: data.marital_status || "",
        father_husband_name: data.father_husband_name || "",
        father_husband_age: data.father_husband_age ?? "",
        occupation: data.occupation || "",
        district: data?.district ?? "",
        emergency_contact_person: data.caretaker_name || "",
        emergency_contact_mobile: data.caretaker_mobile || "",
        relationship: data.caretaker_relationship || "",
        insurance_company_name: data.insurance_company_name || "",
        policy_number: data.policy_number || "",
        guardian: data.guardian || "",
        mother_name: data.mother_name || "",
        mother_age: data.mother_age ?? "",
        old_referral_no: data.old_referral_no || "",
        selectedFatHusName: data.relationship_label || "",
        blood_group: data.blood_group || "",
        patient_id: data?.patient_id ?? "",
        caretaker_id: data?.caretaker_id ?? "",
        refferalDesc: data?.referral_description ?? "",
        rchid: data?.rchid ?? "",
        cityName: (data.city_name ? [data.city_name] : getCitiesOfState(countryCode, stateCode)) || prev.cityName,
      }));
    },
    []
  );

  /** Map by-nxt-id API patient_details to shape expected by assignDemographicsData */
  const mapPatientDetailsFromNxtApi = useCallback((pd) => {
    if (!pd) return {};
    const genderNorm = (g) => (g === "Male" ? "m" : g === "Female" ? "f" : g === "TransGender" ? "t" : g || "");
    return {
      patient_id: pd.id,
      id: pd.id,
      mobile_number: pd.mobile_no ?? "",
      first_name: pd.first_name ?? "",
      last_name: pd.surname ?? "",
      dob: pd.date_of_birth ?? "",
      state_name: pd.state ?? "",
      city_name: pd.city ?? "",
      pincode: pd.pin_code ?? "",
      email: pd.email_id ?? "",
      father_husband_name: pd.father_spouse ?? "",
      caretaker_name: pd.emergency_contact ?? "",
      caretaker_mobile: pd.emergency_mobile ?? "",
      address_line_1: pd.address_01 ?? "",
      address_line_2: pd.address_02 ?? "",
      country: pd.country ?? "India",
      gender: genderNorm(pd.gender),
      title: pd.title ?? "",
      middle_name: pd.middle_name ?? "",
      marital_status: pd.marital_status ?? "",
      mother_name: pd.mother_name ?? "",
      blood_group: pd.blood_group ?? "",
      old_referral_no: pd.old_referral_no ?? "",
      rchid: pd.rch_id ?? "",
    };
  }, []);

  const searchByNxtId = useCallback(() => {
    const nxtId = (formState.nxt_id || "").trim();
    if (!nxtId) {
      errorMessage("Enter NXT ID to search");
      return;
    }
    const url = `${Serviceurls.HISTOPATHOLOGY_BILLING_BILL_BY_NXT_ID}?nxt_id=${encodeURIComponent(nxtId)}`;
    RestAPIService.getAll(url)
      .then((res) => {
        if (res?.data?.status !== "success" || !res?.data?.data) {
          errorMessage(res?.data?.message || "Bill not found");
          return;
        }
        const data = res.data.data;
        const patientDetails = data.patient_details;
        const specimenDetails = data.specimen_details;

        if (patientDetails) {
          const mapped = mapPatientDetailsFromNxtApi(patientDetails);
          setCommonPatientData((prev) => ({ ...prev, ...mapped, patient_id: patientDetails.id, id: patientDetails.id }));
          assignDemographicsData(mapped);
        }

        if (specimenDetails?.sample_id != null) {
          handleChange("sample_id", specimenDetails.sample_id);
        }
        if (data.nxt_id != null) {
          handleChange("nxt_id", data.nxt_id);
        }

        setSearchResultSpecimen(specimenDetails || null);
      })
      .catch((err) => {
        errorMessage(err?.response?.data?.message || err?.message || "Failed to fetch bill");
      });
  }, [formState.nxt_id, errorMessage, mapPatientDetailsFromNxtApi, assignDemographicsData, handleChange]);

  const CreatePatient = useCallback(
    (patientId = null) => {
      if (formState.rchError) return errorMessage("Invalid RCH ID");
      if (formState.employer_id && !formState.employee_id) return errorMessage("Employee Id is Mandatory !");
      if (formState.insurance_company_name && !formState.policy_number) return errorMessage("Insurance Number is Mandatory");

      const dob = formState.dob ? new Date(formState.dob) : null;
      const dateOfBirthStr = dob ? DateTime.fromJSDate(dob).toFormat("yyyy-MM-dd") : "";

      const genderMap = { m: "Male", f: "Female", t: "TransGender" };
      const genderValue = formState.gender ? (genderMap[formState.gender] || formState.gender) : "";

      const payload = {
        mobile_no: formState.mobile_no || "",
        first_name: formState.first_name || "",
        date_of_birth: dateOfBirthStr,
        gender: genderValue,
        age: formState.age != null && formState.age !== "" ? String(formState.age) : "",
        title: formState.title || "",
        middle_name: formState.middle_name || "",
        surname: formState.surname || "",
        address_01: formState.address_1 || "",
        address_02: formState.address_2 || "",
        city: formState.city || "",
        state: formState.state || "",
        country: formState.country || "",
        pin_code: formState.pin_code || "",
        email_id: formState.email_id || "",
        corporate_id: formState.employer_id != null && formState.employer_id !== "" ? Number(formState.employer_id) : "",
        employee_id_no: formState.employee_id || "",
        marital_status: formState.marital_status || "",
        father_spouse: formState.father_husband_name || "",
        father_spouse_age: formState.father_husband_age ?? "",
        mother_name: formState.mother_name || "",
        mother_age: formState.mother_age != null && formState.mother_age !== "" ? String(formState.mother_age) : "",
        occupation: formState.occupation || "",
        district: formState.district || "",
        emergency_contact: formState.emergency_contact_person || "",
        emergency_mobile: formState.emergency_contact_mobile || "",
        relationship: formState.relationship || "",
        blood_group: formState.blood_group || "",
        old_referral_no: formState.old_referral_no || "",
        guardian: formState.guardian || "",
        rch_id: formState.rchid ? CommonValidation.removeSpace(formState.rchid) : "",
      };

      if (patientId != null && patientId !== "") {
        payload.id = patientId;
      }

      const apiUrl = Serviceurls.HISTOPATHOLOGY_BILLING_PATIENT;

      RestAPIService.create(payload, apiUrl)
        .then((res) => {
          if (res?.data?.status === "success") {
            const d = res.data.data || {};
            const createdOrUpdatedId = d.id ?? d.patient_id ?? patientId ?? formState.patient_id;
            if (createdOrUpdatedId) {
              setCommonPatientData((prev) => ({ ...prev, patient_id: createdOrUpdatedId, id: createdOrUpdatedId }));
              setFormState((prev) => ({ ...prev, patient_id: createdOrUpdatedId }));
            }
            if (patientId == null && createdOrUpdatedId) {
              successMessage(res?.data?.message || "Patient created successfully");
            } else if (patientId != null) {
              successMessage(res?.data?.message || "Patient updated successfully");
            }
          }
        })
        .catch((err) => {
          errorMessage(err?.response?.data?.message || err?.message);
        });
    },
    [formState, errorMessage, successMessage]
  );

  const onClearHandler = useCallback(() => {
    clearCachevalue("OP_CreateAppointment_PatientId");
    setSearchResultSpecimen(null);
    const stateName = getCachevalue("state_name");
    const cityName = getCachevalue("city_name");
    const list = getAllStates();
    const match = list?.find((item) => item?.name?.toLowerCase() === (stateName || "").toLowerCase());
    const stateList = getStatesOfCountry("IN");
    const cityList = match ? getCitiesOfState("IN", match.isoCode) : [];
    setFormState((prev) => ({
      ...getInitialFormState(getCachevalue),
      city: cityName,
      cityName: cityList,
      AllStates: stateList,
      state_code: match?.isoCode,
      CorporateList: prev.CorporateList ?? [],
    }));
    setCommonPatientData({});
    setQuickBillClear(true);
    setTimeout(() => setQuickBillClear(false), 100);
  }, []);

  const MandatoryCheck = useCallback(() => {
    const count = Math.min(+mandotoryCount, layoutFeilds?.length ?? 0);
    for (let i = 0; i < count; i++) {
      const field = layoutFeilds[i];
      if (!field) continue;
      const val = formState[field.field_name];
      if (CommonValidation.removeSpace(val) === "" || val == null) {
        errorMessage(`${field.label} is Mandatory !`);
        return false;
      }
      if (field.field_name === "father_husband_name" && val !== "" && !formState.selectedFatHusName) {
        errorMessage("Select Father/Spouse Relationship");
        return false;
      }
    }
    return true;
  }, [formState, layoutFeilds, mandotoryCount, errorMessage]);

  /** Returns Promise<patientId>: resolves with existing id or creates patient and resolves with new id. When patient is selected, calls API with patient_id for update. */
  const getOrCreatePatientId = useCallback(() => {
    const currentId = commonPatientData?.patient_id ?? commonPatientData?.id ?? formState.patient_id;

    if (formState.rchError) {
      errorMessage("Invalid RCH ID");
      return Promise.reject(new Error("Invalid RCH ID"));
    }
    if (formState.employer_id && !formState.employee_id) {
      errorMessage("Employee Id is Mandatory !");
      return Promise.reject(new Error("Employee Id is Mandatory !"));
    }
    if (formState.insurance_company_name && !formState.policy_number) {
      errorMessage("Insurance Number is Mandatory");
      return Promise.reject(new Error("Insurance Number is Mandatory"));
    }
    if (!MandatoryCheck()) {
      return Promise.reject(new Error("Mandatory fields missing"));
    }

    const dob = formState.dob ? new Date(formState.dob) : null;
    const dateOfBirthStr = dob ? DateTime.fromJSDate(dob).toFormat("yyyy-MM-dd") : "";
    const genderMap = { m: "Male", f: "Female", t: "TransGender" };
    const genderValue = formState.gender ? (genderMap[formState.gender] || formState.gender) : "";
    const payload = {
      mobile_no: formState.mobile_no || "",
      first_name: formState.first_name || "",
      date_of_birth: dateOfBirthStr,
      gender: genderValue,
      age: formState.age != null && formState.age !== "" ? String(formState.age) : "",
      title: formState.title || "",
      middle_name: formState.middle_name || "",
      surname: formState.surname || "",
      address_01: formState.address_1 || "",
      address_02: formState.address_2 || "",
      city: formState.city || "",
      state: formState.state || "",
      country: formState.country || "",
      pin_code: formState.pin_code || "",
      email_id: formState.email_id || "",
      corporate_id: formState.employer_id != null && formState.employer_id !== "" ? Number(formState.employer_id) : "",
      employee_id_no: formState.employee_id || "",
      marital_status: formState.marital_status || "",
      father_spouse: formState.father_husband_name || "",
      father_spouse_age: formState.father_husband_age ?? "",
      mother_name: formState.mother_name || "",
      mother_age: formState.mother_age != null && formState.mother_age !== "" ? String(formState.mother_age) : "",
      occupation: formState.occupation || "",
      district: formState.district || "",
      emergency_contact: formState.emergency_contact_person || "",
      emergency_mobile: formState.emergency_contact_mobile || "",
      relationship: formState.relationship || "",
      blood_group: formState.blood_group || "",
      old_referral_no: formState.old_referral_no || "",
      guardian: formState.guardian || "",
      rch_id: formState.rchid ? CommonValidation.removeSpace(formState.rchid) : "",
    };

    if (currentId != null && currentId !== "") {
      payload.id = currentId;
    }

    return RestAPIService.create(payload, Serviceurls.HISTOPATHOLOGY_BILLING_PATIENT)
      .then((res) => {
        if (res?.data?.status === "success") {
          const d = res.data.data || {};
          const createdOrUpdatedId = d.id ?? d.patient_id ?? currentId ?? formState.patient_id;
          if (createdOrUpdatedId) {
            setCommonPatientData((prev) => ({ ...prev, patient_id: createdOrUpdatedId, id: createdOrUpdatedId }));
            setFormState((prev) => ({ ...prev, patient_id: createdOrUpdatedId }));
          }
          return createdOrUpdatedId;
        }
        return Promise.reject(new Error(res?.data?.message || "Patient create failed"));
      })
      .catch((err) => {
        errorMessage(err?.response?.data?.message || err?.message);
        return Promise.reject(err);
      });
  }, [
    commonPatientData?.patient_id,
    commonPatientData?.id,
    formState,
    MandatoryCheck,
    errorMessage,
  ]);

  return (
    <Box component="div" className="eMed_bill_container" sx={{ backgroundColor: "#f0f0f0", paddingTop: "0.5vh" }}>
      <Box component="div" display="flex" flexDirection="column" sx={{ backgroundColor: "white", height: "auto" }}>
        <Accordion expanded={openAccordion} sx={{ zIndex: 3, backgroundColor: "white", height: "9vh" }} elevation={0}>
          <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
            <AccordionSummary
              className="emed_qckbill_box1_common"
              sx={{ paddingTop: -1, paddingBottom: -1, paddingLeft: -1, height: "6vh" }}
              expandIcon={
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenAccordion(!openAccordion);
                  }}
                >
                  <ExpandMoreIcon />
                </IconButton>
              }
            >
              {isExistBilling && (
                <>
                  <Box sx={{ marginTop: "2.5vh", marginLeft: "1vw", width: "12vw" }}>
                    <TextField
                      size="small"
                      fullWidth
                      label="NXT ID"
                      value={formState.nxt_id || ""}
                      onChange={(e) => handleChange("nxt_id", e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          searchByNxtId();
                        }
                      }}
                      error={formState.rchError}
                    />
                  </Box>
                  <Box sx={{ marginTop: "2.5vh", marginLeft: "1vw", width: "12vw" }}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Sample ID"
                      disabled
                      value={formState.sample_id || ""}
                      onChange={(e) => handleChange("sample_id", e.target.value)}
                      error={formState.rchError}
                    />
                  </Box>
                </>
              )}
              {listMandatory.map((item) => renderTextBox(item, "mandatory"))}
            </AccordionSummary>
            <Box>
              <Button
                size="small"
                variant="contained"
                sx={{ textTransform: "capitalize" }}
                onClick={() => {
                  const patientIdToSend = commonPatientData?.patient_id ?? commonPatientData?.id ?? formState.patient_id;
                  if (MandatoryCheck()) CreatePatient(patientIdToSend || undefined);
                }}
              >
                {(commonPatientData?.patient_id || commonPatientData?.id || formState.patient_id) ? "Update" : "Create"}
              </Button>
            </Box>
          </Box>
          <AccordionDetails sx={{ backgroundColor: "white" }}>
            <Box className="eMed_demolayout_left_TextInput" mb={1.3}>
              {remainMandatoryList.map((item) => renderTextBox(item, "mandatory"))}
            </Box>
            <Box className="emed_qckbill_box1_accordian_detail_box">
              <Typography className="eMed_Esstial_Fields_title">Essential Information</Typography>
            </Box>
            <Box className="eMed_demolayout_left_TextInput">
              {nonMandatoryArray.map((item) => renderTextBox(item, "non-mandatory"))}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
      <Box component="div">
        <HistoBillingTbl
          quickBillClear={quickBillClear}
          patientId={commonPatientData?.patient_id ?? commonPatientData?.id ?? formState.patient_id}
          getOrCreatePatientId={getOrCreatePatientId}
          onBillSuccessClear={onClearHandler}
          isExistingBill={isExistBilling}
          searchResultSpecimen={searchResultSpecimen}
        />
      </Box>
      {message.isErrorMsg && (
        <ToastMsg severity="error" msg={message.isErrorMsgText} msgPop={msgClose} />
      )}
      {message.successMsg && (
        <ToastMsg severity="success" msg={message.successMsgText} msgPop={msgClose} />
      )}
    </Box>
  );
}
